import React, { useState } from 'react';
import { Button, Table } from 'reactstrap';
import { sumRemainingClasses, sumRemainingValue, combineTransactions } from './txnChangeShared';

interface Group {
  key: string;
  courseName: string;
  transactions: any[];
}

interface Props {
  groups: Group[];
  /** Called with the ticked transactions of one course. */
  onChange: (transactions: any[]) => void;
}

const txnKey = (t: any) => String(t.order_id || t.id);
const formatDate = (t: any) => String(t.invoice_date || t.createdAt || '').slice(0, 10);

// One card per course. Every transaction with remaining classes is ticked by default; the
// admin can untick some and change the rest together. Fully used transactions are folded away.
const CourseCard: React.FC<{ group: Group; onChange: Props['onChange'] }> = ({ group, onChange }) => {
  const eligible = group.transactions.filter((t) => sumRemainingClasses(t) > 0);
  const used = group.transactions.filter((t) => sumRemainingClasses(t) === 0);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(eligible.map(txnKey)));
  const [showUsed, setShowUsed] = useState(false);

  const picked = eligible.filter((t) => selected.has(txnKey(t)));
  const pickedView = combineTransactions(picked);
  const currency = group.transactions[0]?.currency || '';

  const toggle = (t: any) => {
    const next = new Set(selected);
    next.has(txnKey(t)) ? next.delete(txnKey(t)) : next.add(txnKey(t));
    setSelected(next);
  };

  const renderRow = (t: any, selectable: boolean) => {
    const lines = t.courseList || [];
    const purchased = lines.reduce((acc: number, c: any) => acc + (+c.classesToPurchase || 0), 0);
    const remaining = sumRemainingClasses(t);
    return (
      <tr key={txnKey(t)} style={{ opacity: selectable ? 1 : 0.6 }}>
        <td style={{ width: 40 }}>
          {selectable && (
            <input type="checkbox" checked={selected.has(txnKey(t))} onChange={() => toggle(t)} style={{ height: 18, width: 18 }} />
          )}
        </td>
        <td>
          {formatDate(t)}
          {t.courseChange?.status === 'ACTIVE' && (
            <div style={{ color: '#5a4fa0', fontSize: '0.8rem' }}>
              Course change #{t.courseChange.changeNumber}
              {t.courseChangeDetails?.oldCourseName ? ` (from ${t.courseChangeDetails.oldCourseName})` : ''}
              {t.courseChangeDetails?.changeType === 'STUDENT_TRANSFER' && (
                <div>Transferred from {t.courseChangeDetails.fromStudentName || 'student'} (ID {t.courseChangeDetails.fromStudentId})</div>
              )}
            </div>
          )}
        </td>
        <td>
          {lines.map((c: any, i: number) => (
            <div key={i} style={{ whiteSpace: 'nowrap' }}>
              <b>{c.level || '-'}</b> · {c.unit_price}/class · {c.eligibleRefundClasses || 0} of {c.classesToPurchase} left
            </div>
          ))}
        </td>
        <td>{purchased}</td>
        <td>{Math.max(0, purchased - remaining)}</td>
        <td>{remaining}</td>
        <td>{sumRemainingValue(t).toFixed(2)}</td>
      </tr>
    );
  };

  return (
    <div style={{ marginTop: 12, marginBottom: 16, borderRadius: 10, boxShadow: '0px 0px 9px 0px #bbbbbb', padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>{group.courseName}</div>
          <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
            {eligible.length
              ? <>Selected: <b>{sumRemainingClasses(pickedView)} classes · {sumRemainingValue(pickedView).toFixed(2)} {currency}</b> from {picked.length} of {eligible.length} transaction{eligible.length > 1 ? 's' : ''}</>
              : 'No remaining classes'}
          </div>
        </div>
        {eligible.length > 0 && (
          <Button color="primary" disabled={!picked.length} onClick={() => onChange(picked)}>
            Change this course
          </Button>
        )}
      </div>

      {(eligible.length > 0 || showUsed) && (
        <Table bordered size="sm" style={{ marginTop: 10, marginBottom: 0 }}>
          <thead>
            <tr>
              <th></th>
              <th>Date</th>
              <th>Levels</th>
              <th>Purchased</th>
              <th>Consumed</th>
              <th>Remaining</th>
              <th>Remaining Value ({currency})</th>
            </tr>
          </thead>
          <tbody>
            {eligible.map((t) => renderRow(t, true))}
            {showUsed && used.map((t) => renderRow(t, false))}
          </tbody>
        </Table>
      )}

      {used.length > 0 && (
        <button
          type="button"
          onClick={() => setShowUsed(!showUsed)}
          style={{ marginTop: 8, background: 'none', border: 'none', padding: 0, color: '#5a4fa0', fontSize: '0.85rem', cursor: 'pointer' }}
        >
          {showUsed ? 'Hide' : 'Show'} {used.length} fully used transaction{used.length > 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
};

const CourseGroupList: React.FC<Props> = ({ groups, onChange }) => {
  if (groups.length === 0) {
    return <p className="text-center" style={{ marginTop: 20 }}>No transactions found.</p>;
  }
  return (
    <>
      {groups.map((g) => (
        // Keyed by its transactions too, so the default ticks reset after a reload.
        <CourseCard key={`${g.key}:${g.transactions.map(txnKey).join(',')}`} group={g} onChange={onChange} />
      ))}
    </>
  );
};

export default CourseGroupList;
